import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/HealthMonitor.css';

interface HealthStatus {
  isHealthy: boolean;
  timestamp: string;
  responseTime?: number;
  error?: string;
}

interface APIEndpointStatus {
  endpoint: string;
  name: string;
  status: HealthStatus;
}

interface HealthMonitorData {
  backendStatus: HealthStatus;
  apiEndpoints: APIEndpointStatus[];
  lastUpdated: string;
  uptime: {
    backend: string;
    overallUptime: string;
  };
}

interface HealthMonitorProps {
  currentUser?: {
    username: string;
    role: 'user' | 'admin';
  };
}

const HealthMonitor: React.FC<HealthMonitorProps> = ({ currentUser }) => {
  const { t } = useTranslation();
  const [healthData, setHealthData] = useState<HealthMonitorData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const performFallbackHealthCheck = async (): Promise<HealthMonitorData> => {
    const backendUrl = process.env.REACT_APP_API_BASE_URL || 'https://portal.infrasec.se/api';
    const startTime = Date.now();
    
    // Check backend health
    let backendStatus: HealthStatus;
    try {
      const response = await fetch(`${backendUrl}/health`, {
        method: 'GET',
        timeout: 10000,
      } as RequestInit);
      
      const responseTime = Date.now() - startTime;
      backendStatus = {
        isHealthy: response.ok,
        timestamp: new Date().toISOString(),
        responseTime,
        error: response.ok ? undefined : `HTTP ${response.status}`
      };
    } catch (err) {
      backendStatus = {
        isHealthy: false,
        timestamp: new Date().toISOString(),
        responseTime: Date.now() - startTime,
        error: (err as Error).message
      };
    }

    // Check critical API endpoints
    const apiEndpoints = [
      { endpoint: '/user', name: 'User Service' },
      { endpoint: '/company', name: 'Company Service' },
      { endpoint: '/getTestSuitesByCompany', name: 'Test Suites' },
      { endpoint: '/getTestDetails', name: 'Test Details' },
      { endpoint: '/documents', name: 'Documents' },
      { endpoint: '/notifications', name: 'Notifications' },
      { endpoint: '/getCertifications', name: 'Certifications' }
    ];

    const apiStatuses: APIEndpointStatus[] = [];
    
    for (const api of apiEndpoints) {
      const apiStartTime = Date.now();
      try {
        const response = await fetch(`${backendUrl}${api.endpoint}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 5000,
        } as RequestInit);
        
        const responseTime = Date.now() - apiStartTime;
        apiStatuses.push({
          endpoint: api.endpoint,
          name: api.name,
          status: {
            isHealthy: response.status < 500, // 4xx errors are expected for test calls, 5xx are problems
            timestamp: new Date().toISOString(),
            responseTime,
            error: response.status >= 500 ? `HTTP ${response.status}` : undefined
          }
        });
      } catch (err) {
        apiStatuses.push({
          endpoint: api.endpoint,
          name: api.name,
          status: {
            isHealthy: false,
            timestamp: new Date().toISOString(),
            responseTime: Date.now() - apiStartTime,
            error: (err as Error).message
          }
        });
      }
    }

    return {
      backendStatus,
      apiEndpoints: apiStatuses,
      lastUpdated: new Date().toISOString(),
      uptime: {
        backend: backendStatus.isHealthy ? 'Online' : 'Offline',
        overallUptime: calculateOverallUptime(backendStatus, apiStatuses)
      }
    };
  };

  const calculateOverallUptime = (backendStatus: HealthStatus, apiStatuses: APIEndpointStatus[]): string => {
    if (!backendStatus.isHealthy) return 'Backend Offline';
    
    const healthyApis = apiStatuses.filter(api => api.status.isHealthy).length;
    const totalApis = apiStatuses.length;
    const percentage = Math.round((healthyApis / totalApis) * 100);
    
    return `${percentage}% APIs Healthy`;
  };

  const getStatusColor = (isHealthy: boolean): string => {
    return isHealthy ? '#10B981' : '#EF4444'; // green-500 : red-500
  };

  const getStatusText = (isHealthy: boolean): string => {
    return isHealthy ? 'Healthy' : 'Unhealthy';
  };

  const formatResponseTime = (responseTime?: number): string => {
    if (!responseTime) return 'N/A';
    return `${responseTime}ms`;
  };

  const formatTimestamp = (timestamp: string): string => {
    return new Date(timestamp).toLocaleString();
  };

  // Auto-refresh effect
  useEffect(() => {
    const loadHealthData = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await performFallbackHealthCheck();
        setHealthData(data);
        setLastRefresh(new Date());
      } catch (err) {
        console.error('Failed to load health data:', err);
        setError('Failed to load health data');
      } finally {
        setLoading(false);
      }
    };

    loadHealthData();
    
    if (autoRefresh) {
      const interval = setInterval(loadHealthData, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const handleManualRefresh = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await performFallbackHealthCheck();
      setHealthData(data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to refresh health data:', err);
      setError('Failed to refresh health data');
    } finally {
      setLoading(false);
    }
  };

  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  if (loading && !healthData) {
    return (
      <div className="health-monitor">
        <div className="health-monitor-header">
          <h1>System Health Monitor</h1>
        </div>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading health status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="health-monitor">
      <div className="health-monitor-header">
        <h1>System Health Monitor</h1>
        <div className="health-monitor-controls">
          <div className="auto-refresh-toggle">
            <label>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={toggleAutoRefresh}
              />
              Auto-refresh (30s)
            </label>
          </div>
          <button 
            className="refresh-button"
            onClick={handleManualRefresh}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh Now'}
          </button>
        </div>
      </div>

      {error && (
        <div className="health-error">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {healthData && (
        <div className="health-content">
          <div className="health-summary">
            <div className="summary-card">
              <h3>Last Updated</h3>
              <p>{formatTimestamp(healthData.lastUpdated)}</p>
            </div>
            <div className="summary-card">
              <h3>Backend Status</h3>
              <p style={{ color: getStatusColor(healthData.backendStatus.isHealthy) }}>
                {healthData.uptime.backend}
              </p>
            </div>
            <div className="summary-card">
              <h3>Overall Health</h3>
              <p>{healthData.uptime.overallUptime}</p>
            </div>
          </div>

          <div className="health-sections">
            {/* Backend Health Section */}
            <div className="health-section">
              <h2>Backend Health</h2>
              <div className="health-item">
                <div className="health-item-header">
                  <span 
                    className="status-indicator"
                    style={{ backgroundColor: getStatusColor(healthData.backendStatus.isHealthy) }}
                  ></span>
                  <span className="service-name">Backend Service</span>
                  <span className="status-text">
                    {getStatusText(healthData.backendStatus.isHealthy)}
                  </span>
                </div>
                <div className="health-item-details">
                  <span>Response Time: {formatResponseTime(healthData.backendStatus.responseTime)}</span>
                  <span>Last Check: {formatTimestamp(healthData.backendStatus.timestamp)}</span>
                  {healthData.backendStatus.error && (
                    <span className="error-message">Error: {healthData.backendStatus.error}</span>
                  )}
                </div>
              </div>
            </div>

            {/* API Endpoints Section */}
            <div className="health-section">
              <h2>API Endpoints</h2>
              <div className="api-endpoints-grid">
                {healthData.apiEndpoints.map((api, index) => (
                  <div key={index} className="health-item">
                    <div className="health-item-header">
                      <span 
                        className="status-indicator"
                        style={{ backgroundColor: getStatusColor(api.status.isHealthy) }}
                      ></span>
                      <span className="service-name">{api.name}</span>
                      <span className="status-text">
                        {getStatusText(api.status.isHealthy)}
                      </span>
                    </div>
                    <div className="health-item-details">
                      <span>Endpoint: {api.endpoint}</span>
                      <span>Response Time: {formatResponseTime(api.status.responseTime)}</span>
                      <span>Last Check: {formatTimestamp(api.status.timestamp)}</span>
                      {api.status.error && (
                        <span className="error-message">Error: {api.status.error}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="health-footer">
            <p>
              <span className="footer-info">
                Monitoring active since {formatTimestamp(lastRefresh.toISOString())} • 
                Next refresh in {autoRefresh ? '30 seconds' : 'manual'}
              </span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthMonitor;